import React, { useMemo, useState } from "react";
import { deleteCompetitionMatch } from "../../../../api";
import { CompetitionStage, ID, Match, TournamentParticipant } from "../../../../types";
import { MatchesTable } from "./MatchesTable";
import { Button } from "../../Button";
import { useTournamentEvent } from "../TournamentEventContext";


const labelsByDelta: Record<number, string> = {
  16: "Ottavi",
  8: "Quarti",
  4: "Semifinali",
  2: "Finali",
};

interface CompetitionStageKnockoutProps {
  stage: CompetitionStage;
  stageMatches: Match[];
  participantsById: Map<ID, TournamentParticipant>;
  assignableParticipants: TournamentParticipant[];
}

export const CompetitionStageKnockout: React.FC<CompetitionStageKnockoutProps> = ({
  stage,
  stageMatches,
  participantsById,
  assignableParticipants,
}) => {
  const { canManage, onResultSaved } = useTournamentEvent();
  const [bracketsIsOpen, setBracketsIsOpen] = useState<Record<number, boolean>>({});
  
  const stageMatchesMap = useMemo(() => new Map(stageMatches.map(m => [m.id, m])), [stageMatches]);
  
  const prevWinnerBySlot = useMemo(() => {
    const map = new Map<string, Match>();
    stageMatches.forEach((match) => {
      if (match.next_winner_match && match.next_winner_slot) {
        map.set(`${match.next_winner_match}-${match.next_winner_slot}`, match);
      }
    });
    return map;
  }, [stageMatches]);

  const prevLoserBySlot = useMemo(() => {
    const map = new Map<string, Match>();
    stageMatches.forEach((match) => {
      if (match.next_loser_match && match.next_loser_slot) {
        map.set(`${match.next_loser_match}-${match.next_loser_slot}`, match);
      }
    });
    return map;
  }, [stageMatches]);

  const bracketMatches: Array<Array<Match[]>> = useMemo(() => {
    // Costruisco un map dove la chiave è il numero di match per fase (delta)
    // La seconda chiave è la posizione di inizio della classifica
    const brackets: Record<number, Record<number, Match[]>> = {}
    stageMatches.forEach(match => {
      const delta = (match.ranking_end_position ?? 0) - (match.ranking_start_position ?? 0) + 1;
      if (!brackets[delta]) {
        brackets[delta] = {};
      }
      if (!brackets[delta][match.ranking_start_position ?? 0]) {
        brackets[delta][match.ranking_start_position ?? 0] = [];
      }
      brackets[delta][match.ranking_start_position ?? 0].push(match);
      brackets[delta][match.ranking_start_position ?? 0].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    })
    // Ordino le chiavi del numero di match in modo decrescente
    const sortedKeysBrackets = Object.keys(brackets).sort((a, b) => parseInt(b) - parseInt(a));
    const sortedBrackets: Array<Array<Match[]>> = []; // output
    sortedKeysBrackets.forEach((key, index) => {
      sortedBrackets.push([]);
      // Ordino le chiavi delle posizioni di inizio della classifica in modo crescente
      const blockMatches: Record<number, Match[]> = brackets[parseInt(key)];
      const sortedKeysMatches = Object.keys(blockMatches).sort((a, b) => parseInt(a) - parseInt(b));

      sortedKeysMatches.forEach(keyMatch => {
        const matches: Match[] = blockMatches[parseInt(keyMatch)];
        sortedBrackets[index].push(matches);
      })
    })
    return sortedBrackets;
  }, [stageMatches]);

  React.useEffect(() => {
    // Initialize all brackets as open
    const initialOpen: Record<number, boolean> = {};
    let previousIsPlayed = true;
    bracketMatches.forEach((matchStage, index) => {
      const isOngoing = matchStage.some(matchSblock => matchSblock.some(match => match.status === 'ongoing'));
      const isPlayed = matchStage.every(matchSblock => matchSblock.every(match => match.status === 'played'));
      const isNotStarted = !isOngoing && !isPlayed;
      if (isOngoing || (isNotStarted && previousIsPlayed)) {
        initialOpen[index] = true;
      } else {
        initialOpen[index] = false;
      }
      previousIsPlayed = isPlayed;
    });
    setBracketsIsOpen(initialOpen);
  }, [bracketMatches]);

  const getSlotLabel = (match: Match, slot: 1 | 2) => {
    const winnerPrev = prevWinnerBySlot.get(`${match.id}-${slot}`);
    if (winnerPrev) {
      return `Vincente ${winnerPrev.order ?? winnerPrev.id}`;
    }
    const loserPrev = prevLoserBySlot.get(`${match.id}-${slot}`);
    if (loserPrev) {
      return `Perdente ${loserPrev.order ?? loserPrev.id}`;
    }
    return "TBD";
  };

  const deletePlacementBlock = async (matches: Match[], title: string) => {
    const confirmed = window.confirm(
      `Vuoi eliminare tutti i match del blocco ${title}?`
    );
    if (!confirmed) return;

    try {
      await Promise.all(matches.map((match) => deleteCompetitionMatch(match.id)));
      await onResultSaved();
    } catch (error) {
      console.error(error);
      alert("Errore nell'eliminazione del blocco di piazzamento.");
    }
  };

  if (stageMatches.length === 0) {
    return null;
  }

  return (
    <div className="space-y-5">
      {bracketMatches.map((block, index) => {
        const firstMatchBlock = block[0][0];
        const rkStartBlock = firstMatchBlock?.ranking_start_position ?? 0;
        const rkEndBlock = firstMatchBlock?.ranking_end_position ?? 0;
        const delta = rkEndBlock - rkStartBlock + 1;
        const isScheduled = block.every(matchSblock => matchSblock.every(match => match.status === 'scheduled'));
        const isPlayed = block.every(matchSblock => matchSblock.every(match => match.status === 'played'));
        const statusText = isScheduled ? 'Programmate' : isPlayed ? 'Concluso' : 'In corso';
        return (
          <div key={`bracket-container-${index}`} className="bg-white border border-gray-300 rounded shadow-sm p-5 mt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center justify-start gap-2">
                <h3 className="font-semibold mb-0">{labelsByDelta[delta] || `Bracket ${index}`}</h3>
                <i className="text-xs">({statusText})</i>
              </div>
              <Button 
                variant="secondary"
                size="xs"
                onClick={() => setBracketsIsOpen({...bracketsIsOpen, [index]: !bracketsIsOpen[index]})}
              >
                {bracketsIsOpen[index] ? '▼' : '▶'}
              </Button>
            </div>
            {block.map((matches, position) => {
              const firstMatch = matches[0];
              const rkStart = firstMatch?.ranking_start_position ?? 0;
              const rkEnd = firstMatch?.ranking_end_position ?? 0;
              const title = rkStart === 1 ? '' : `Piazzamento ${rkStart}-${rkEnd}`;
              const canDeleteBlock = canManage && rkStart > 1;
              return (
                <div 
                  key={`bracket-position-${index}-${position}`}
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${bracketsIsOpen[index] ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}
                >
                  <div className={position === 0 ? "" : "mt-3"}>
                    {title && (
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <h4 className="font-semibold">{title}</h4>
                        {canDeleteBlock && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => deletePlacementBlock(matches, title)}
                          >
                            Elimina blocco
                          </Button>
                        )}
                      </div>
                    )}
                    <MatchesTable
                      key={`bracket-${index}-${position}`}
                      title=""
                      stageMatches={stageMatchesMap}
                      matches={matches}
                      participantsById={participantsById}
                      participants={assignableParticipants}
                      getSlotLabel={getSlotLabel}
                    />
                  </div>
                </div>
              )
            })
            }
          </div>
        )
      })}
    </div>
  );
};
